/**
 * Context de Tema (Claro/Escuro)
 * Gerencia o estado do tema em toda a aplicação
 */

import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  // Carrega o tema salvo do localStorage (padrão: claro)
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme === 'dark';
  });

  // Salva a preferência no localStorage quando mudar
  useEffect(() => {
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  const toggleTheme = () => {
    setIsDarkMode((prev) => !prev);
  };

  // Cria o tema do Material-UI baseado no modo
  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: isDarkMode ? 'dark' : 'light',
          primary: {
            main: '#007bff',
          },
          secondary: {
            main: '#6c757d',
          },
          success: {
            main: '#00B389',
          },
          error: {
            main: '#FF4873',
          },
          background: {
            default: isDarkMode ? '#12151b' : '#f8f8f8',
            paper: isDarkMode ? '#1a1f26' : '#ffffff',
          },
          text: {
            primary: isDarkMode ? '#d3dcea' : '#333333',
            secondary: isDarkMode ? '#a0a0a0' : '#6c757d',
          },
          divider: isDarkMode ? '#1f2530' : '#dee2e6',
        },
        shape: {
          borderRadius: 8, // Border radius padrão para todos os componentes
        },
        components: {
          MuiCard: {
            styleOverrides: {
              root: {
                borderRadius: isDarkMode ? 12 : 8,
                boxShadow: isDarkMode
                  ? '0 0 6px rgba(0, 0, 0, 0.35)'
                  : '0 1px 3px rgba(0, 0, 0, 0.08)',
                border: isDarkMode ? '1px solid #12151b' : '1px solid #dee2e6',
                backgroundColor: isDarkMode ? '#1a1f26' : '#ffffff',
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundImage: 'none',
                borderRadius: 8,
              },
            },
          },
          MuiAppBar: {
            styleOverrides: {
              root: {
                backgroundColor: isDarkMode ? '#1a1f26' : '#ffffff',
                color: isDarkMode ? '#d3dcea' : '#333333',
                boxShadow: isDarkMode 
                  ? '0 0 6px rgba(0, 0, 0, 0.35)' 
                  : '0 1px 3px rgba(0, 0, 0, 0.08)',
                borderBottom: isDarkMode ? '1px solid #1f2530' : '1px solid #dee2e6',
              },
            },
          },
          MuiDrawer: {
            styleOverrides: {
              paper: {
                backgroundColor: isDarkMode ? '#1a1f26' : '#ffffff',
                borderRight: isDarkMode ? '1px solid #1f2530' : '1px solid #dee2e6',
                boxShadow: isDarkMode 
                  ? '2px 0 6px rgba(0, 0, 0, 0.35)' 
                  : '2px 0 5px rgba(0, 0, 0, 0.05)',
              },
            },
          },
          MuiButton: {
            styleOverrides: {
              root: {
                borderRadius: 6,
              },
            },
          },
          MuiTextField: {
            styleOverrides: {
              root: {
                '& .MuiOutlinedInput-root': {
                  borderRadius: 6,
                },
              },
            },
          },
        },
      }),
    [isDarkMode]
  );

  const value = {
    isDarkMode,
    toggleTheme,
    theme,
  };

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
